import React, { useState, useEffect, useMemo } from "react";
import { Select, Spin, Tag, message, Tooltip } from "antd";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import PeoplePickerList from "./PeoplePickerList/PeoplePickerList";
import { fetchUsersByInput } from "../../../util/service";

const DROPDOWN_DEFAULT = { SELECT_AN_OPTION: "Start typing to search for a user..." };
const CALLBACK_TIMEOUT_DURATION = 500;
const SUGGEST_PEOPLE_CHAR_LENGTH = 3;

export default function MultiSelectPeoplePicker({
  selectedUsersData,
  onChange,
  selectionLimit,
  disabled,
  currentUserEmail,
  disabledOptions = [],
}) {
  const [loading, setLoading] = useState(false);
  const [suggestedPeople, setSuggestedPeople] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);

  useEffect(() => {
    if (selectedUsersData) {
      setSelectedValues(selectedUsersData);
    }
  }, [selectedUsersData]);

  const fetchUsers = async (inputValue) => {
    if (inputValue && inputValue.length >= SUGGEST_PEOPLE_CHAR_LENGTH) {
      setLoading(true);
      try {
        const result = await fetchUsersByInput(inputValue);
        if (result && result.value) {
          const formattedList = result.value.map(item => ({
            email_id: item?.mail,
            name: item?.displayName,
            system_id: item?.onPremisesSamAccountName,
            job_title: item?.jobTitle,
          }));
          setSuggestedPeople(formattedList);
        }
      } catch (error) {
        console.error("Failed to fetch users:", error);
        message.error("Failed to search for users.");
      } finally {
        setLoading(false);
      }
    } else {
      setSuggestedPeople([]);
    }
  };

  const debouncedFetchUsers = useMemo(() => debounce(fetchUsers, CALLBACK_TIMEOUT_DURATION), []);

  const handleSearch = (inputValue) => {
    debouncedFetchUsers(inputValue);
  };

  const handleChange = (selectedEmails) => {
    const allAvailablePeople = new Map([...selectedValues, ...suggestedPeople].map(p => [p.email_id, p]));
    
    let newSelectedValues = selectedEmails
        .map(email => allAvailablePeople.get(email))
        .filter(Boolean);

    // FIX: Ensure the current user cannot be removed by backspace or any other means.
    // This check runs every time the selection changes.
    if (currentUserEmail && !newSelectedValues.some(user => user.email_id === currentUserEmail)) {
        // Find the full user object from the component's state before the change.
        const currentUserObject = selectedValues.find(user => user.email_id === currentUserEmail);
        if (currentUserObject) {
            // If the user was removed, add them back to the beginning of the array.
            newSelectedValues.unshift(currentUserObject);
        }
    }

    if (newSelectedValues.length > selectionLimit) {
      message.warning(`You can only select up to ${selectionLimit} users.`);
      return;
    }
    
    setSelectedValues(newSelectedValues);
    onChange(newSelectedValues);
  };

  const tagRender = (props) => {
    const { label, value, onClose } = props;
    const isCurrentUser = value === currentUserEmail;
    
    return (
      <Tag
        closable={!disabled && !isCurrentUser}
        onClose={onClose}
        style={{ margin: "2px" }}
      >
        {label}
      </Tag>
    );
  };

  const memoizedOptions = useMemo(() => {
    // FIX: Combine selected users with search suggestions.
    // This ensures that even on initial render, the Select component has an option
    // to match the value (email) and display the correct label (name).
    const combinedPeople = [...selectedValues, ...suggestedPeople];
    const uniquePeopleMap = new Map(combinedPeople.map(p => [p.email_id, p]));

    const disabledEmails = new Set(disabledOptions);
    
    return Array.from(uniquePeopleMap.values()).map(user => {
      const isOwner = disabledEmails.has(user.email_id);
      const isDisabled = isOwner;

      return {
        value: user.email_id,
        label: user.name, // This is what's displayed in the tag and dropdown
        disabled: isDisabled,
        title: isOwner ? `'${user.name}' is already part of admin.` : user.name,
        user,
      };
    });
  }, [suggestedPeople, selectedValues, disabledOptions]);

  return (
    <Select
      mode="multiple"
      style={{ width: "100%" }}
      placeholder={DROPDOWN_DEFAULT.SELECT_AN_OPTION}
      value={selectedValues.map(user => user.email_id)}
      onChange={handleChange}
      onSearch={handleSearch}
      loading={loading}
      disabled={disabled}
      tagRender={tagRender}
      filterOption={false}
      notFoundContent={loading ? <Spin size="small" /> : null}
      options={memoizedOptions}
      optionRender={(option) => (
        <Tooltip title={option.data.title} placement="left">
            <div>
                <PeoplePickerList option={option.data.user} />
            </div>
        </Tooltip>
      )}
    />
  );
}

MultiSelectPeoplePicker.propTypes = {
  selectedUsersData: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  selectionLimit: PropTypes.number,
  disabled: PropTypes.bool,
  currentUserEmail: PropTypes.string,
  disabledOptions: PropTypes.arrayOf(PropTypes.string),
};

MultiSelectPeoplePicker.defaultProps = {
  selectedUsersData: [],
  selectionLimit: 10,
  disabled: false,
  currentUserEmail: null,
  disabledOptions: [],
};
